import {literalUnion, nullable} from '@raison/io-ts-tools';
import {AccountId} from '@raison/types';
import {isRight} from 'fp-ts/Either';
import {array, exact, partial, string, TypeOf} from 'io-ts';
import QueryString from 'query-string';
import {useCallback, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {TransactionGroupName, TransactionStatus} from './TransactionSchema';

export namespace TransactionsHistory {
    export type MoreFilter = (typeof MoreFilter)[keyof typeof MoreFilter];
    export const MoreFilter = {
        lowValueTxn: 'show_low_value_transactions',
    } as const;

    const codec = partial({
        account: array(AccountId),
        wallet: array(AccountId),
        token: array(AccountId),
        status: array(TransactionStatus),
        group: array(TransactionGroupName),
        date: nullable(string),
        more: array(literalUnion(...Object.values(MoreFilter))),
    });

    export type Filters = TypeOf<typeof codec>;

    export interface FiltersWithPagination extends Filters {
        last?: number;
        limit?: number;
    }

    export const filtersToSearch = (filters: Filters) => {
        return QueryString.stringify(filters, {arrayFormat: 'bracket', skipNull: true});
    };

    export const filtersToUrlParams = (filters: FiltersWithPagination) => {
        const params = {
            ...filters,
            /**
             * FIXME: Backward-compatibility workaround: API returns all orders by default, but frontend implicitly asks for hiding low-value ones.
             * @see https://jira.raison.digital/browse/RAISON-6245
             */
            more: filters.more?.filter(flag => flag !== MoreFilter.lowValueTxn),
            hide_low_value_transactions: !filters.more?.includes(MoreFilter.lowValueTxn),
        };

        return QueryString.stringify(params, {arrayFormat: 'bracket', skipNull: true});
    };

    export const decodeFilters = (search: string) =>
        QueryString.parse(search, {arrayFormat: 'bracket', parseNumbers: true});

    export const useFilters = () => {
        const {search} = useLocation();

        const parsedFilters = useMemo(() => {
            const decodedFilters = exact(codec).decode(decodeFilters(search));

            if (isRight(decodedFilters)) {
                return decodedFilters.right;
            } else {
                return {};
            }
        }, [search]);

        const navigate = useNavigate();

        const apply = useCallback(
            (filters: Partial<Filters>) => {
                navigate({search: filtersToSearch({...parsedFilters, ...filters})}, {replace: true});
            },
            [history, parsedFilters]
        );

        const resetAll = useCallback(() => {
            navigate({search: ''}, {replace: true});
        }, [history]);

        return [parsedFilters, {apply, resetAll}] as const;
    };
}
